export interface ArchiveGallery {
  columns: number;
  gap: number;
  cardWidth: number;
  cardHeight: number;
}
export function archiveGallery(theme: any, device: string): ArchiveGallery {
  return {
    columns: device === "mobile" ? 1 : 3,
    gap: 24,
    cardWidth: 390,
    cardHeight: 488,
    ...theme.archiveGallery?.[device],
  };
}
export function validateArchiveGallery(value: any) {
  if (value === undefined) return undefined;
  if (!value || typeof value !== "object")
    throw Error("Invalid card list layout");
  return Object.fromEntries(
    ["desktop", "mobile"].map((device) => {
      const settings = archiveGallery({ archiveGallery: value }, device);
      for (const [key, min, max] of [
        ["columns", 1, 6],
        ["gap", 0, 120],
        ["cardWidth", 160, 1200],
        ["cardHeight", 120, 1800],
      ] as const) {
        const number = settings[key];
        if (
          !Number.isFinite(number) ||
          number < min ||
          number > max ||
          (key === "columns" && !Number.isInteger(number))
        )
          throw Error("Invalid card list " + key);
      }
      return [device, settings];
    }),
  );
}
export function archiveGalleryStyle(theme: any, device = "") {
  const desktop = archiveGallery(
    theme,
    device === "mobile" ? "mobile" : "desktop",
  );
  const mobile = archiveGallery(
    theme,
    device === "desktop" ? "desktop" : "mobile",
  );
  return `--gallery-columns:${desktop.columns};--gallery-mobile-columns:${mobile.columns};--gallery-gap:${desktop.gap}px;--gallery-mobile-gap:${mobile.gap}px;--card-width:${desktop.cardWidth}px;--card-mobile-width:${mobile.cardWidth}px`;
}

export const responsiveArchiveGridCss = `display:grid;--active-columns:var(--gallery-columns,3);--active-gap:clamp(12px,calc(var(--gallery-gap,24px)*var(--artwork-scale,1)),180px);grid-template-columns:repeat(auto-fit,minmax(min(100%,max(280px,calc((100% - (var(--active-columns) - 1)*var(--active-gap))/var(--active-columns)))),1fr));gap:var(--active-gap);align-items:start`;

export const archiveDesignCss = `
.scene:has(.archive-gallery),.layout-scene:has(.archive-gallery){aspect-ratio:auto;display:flow-root}
.safe-box-layer:has(>.archive-gallery){position:relative;top:0;aspect-ratio:auto;display:flow-root}
.image-safe-box.archive-gallery{position:relative!important;left:0!important;top:0!important;width:var(--safe-w);height:auto!important;margin-left:var(--safe-x);margin-top:calc(var(--safe-flow-y)*var(--artwork-ratio,.5625)*1cqw);overflow:visible}
.safe-box-layer[data-device="mobile"]>.archive-gallery{width:var(--safe-mw);margin-left:var(--safe-mx);margin-top:calc(var(--safe-mflow-y)*var(--artwork-ratio,.5625)*1cqw)}
.archive-gallery>.page-blocks,.archive-gallery>.page-blocks>.page-block{height:auto!important;min-height:0!important}
.archive-gallery .archive-grid,.archive-grid{${responsiveArchiveGridCss}}
.card.card-artboard,.card.card-artboard.artwork-layout{position:relative;display:block!important;container-type:inline-size;aspect-ratio:auto;padding:0!important;min-width:0;max-width:max(280px,calc(var(--card-width,390px)*var(--page-artwork-scale,var(--artwork-scale,1))));width:100%;background:transparent!important;border:0!important;box-shadow:none!important;transform:none!important;--artwork-offset:0px}
.image-safe-box.card-frame{position:relative!important;left:0!important;top:0!important;margin-left:var(--safe-x);margin-top:calc(var(--safe-flow-y)*var(--card-reference-ratio,1.25128)*1cqw);width:var(--safe-w);height:auto!important;aspect-ratio:var(--frame-aspect);overflow:visible;isolation:isolate}
.card-fill{position:absolute;inset:0;z-index:0;pointer-events:none}
.card-fill>.page-blocks,.card-fill .page-block{height:100%;margin:0}
.card-fill .layout-shape{width:100%;height:100%;min-height:0}
.card-content-anchor{position:absolute;inset:0;pointer-events:none;z-index:1}
.card-content-anchor>.safe-box-layer{inset:0;height:100%;aspect-ratio:auto}
.card-content-anchor .image-safe-box{overflow:visible}
.card-content-anchor .image-safe-box:not([data-shape])>.page-blocks>.page-block{background:var(--active-fill);border:var(--active-stroke) var(--active-stroke-style) var(--active-stroke-color);border-radius:var(--br)}
.card-content-anchor .image-safe-box:not([data-shape])>.page-blocks>.page-block:has(.layout-button){background:transparent;border:0}
.archive-gallery:not([data-layer-selected])>.safe-box-edge,.archive-gallery:not([data-layer-selected])>.safe-box-resize,.card-frame:not([data-layer-selected])>.safe-box-edge,.card-frame:not([data-layer-selected])>.safe-box-resize{pointer-events:none;opacity:0}
.archive-group-handle{position:absolute;left:0;top:-24px;z-index:30;display:none;border:0!important;border-radius:3px!important;min-height:20px!important;padding:2px 6px!important;font:11px/16px system-ui!important;color:white!important;background:#0076ab!important;cursor:move;touch-action:none;transform:scale(var(--studio-inverse-scale,1));transform-origin:bottom left;white-space:nowrap}
.image-safe-box[data-layer-selected]>.archive-group-handle{display:block}
.page-content:has(.archive-grid){position:relative;inset:auto}
.layer-branch{margin:3px 0 8px 10px;border-left:1px solid var(--studio-line,#596461);padding-left:6px}
.layer-branch-title{font-size:11px;padding:5px 8px;color:var(--ui-muted,#bac6c1)}
.layer-branch .element-row{display:grid!important;grid-template-columns:minmax(0,1fr) 28px 22px;gap:3px;padding:6px 2px!important}.layer-branch .element-row::before{display:none}.layer-branch [data-up-element],.layer-branch [data-down-element]{display:none}.layer-branch .element-name{width:auto!important;min-width:0;font-size:11px!important;line-height:1.4!important;white-space:normal}.layer-branch [data-hide-element]{font-size:9px!important}
@media(max-width:640px){
 .archive-gallery{width:var(--safe-mw)!important;margin-left:var(--safe-mx)!important;margin-top:calc(var(--safe-mflow-y)*var(--artwork-ratio,.5625)*1cqw)!important}
 .archive-grid{--active-columns:var(--gallery-mobile-columns,1);--active-gap:clamp(12px,calc(var(--gallery-mobile-gap,24px)*var(--artwork-scale,1)),180px)}
 .card.card-artboard{max-width:max(280px,calc(var(--card-mobile-width,390px)*var(--page-artwork-scale,var(--artwork-scale,1))))}
 .card-content-anchor .image-safe-box:not([data-shape])>.page-blocks>.page-block{border-radius:var(--mr)}
}
.layout-canvas.mobile .archive-grid{--active-columns:var(--gallery-columns,1);--active-gap:clamp(12px,calc(var(--gallery-gap,24px)*var(--artwork-scale,1)),180px)}
`;

export const archiveEditorCss = `.element-toolbar [hidden]{display:none!important}.studio-stage .layout-panel:not([data-selected-page-layer="reading-box"])>.canvas-box-tools,.studio-stage .layout-panel:not([data-selected-page-layer="reading-box"])>.canvas-panel-resize-handle{opacity:0;pointer-events:none}`;
